/**
 * Wishlist Toggle Action
 * Triggered by: arc:action:wishlistToggle
 *
 * Hydrates a local wishlist cache from Smart Wishlist Public API once per
 * page load (deferred), then uses local state for instant button updates.
 */

const STORAGE_KEY = 'arc_wishlist_items';
const WISHLIST_TOGGLE_SELECTOR = '[data-wishlist-toggle-button="true"]';

let publicWishlistCachePromise = null;
let hasScheduledPublicWishlistHydration = false;
let lastLocalMutationAt = 0;

const readWishlistStorage = () => {
  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('wishlistToggle: failed reading storage', error);
    return [];
  }
};

const writeWishlistStorage = (items) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.warn('wishlistToggle: failed writing storage', error);
  }
};

const toUniqueWishlistKeys = (items = []) => {
  const keys = [];
  const seen = new Set();

  items.forEach((value) => {
    const key = String(value || '').trim();
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    keys.push(key);
  });

  return keys;
};

const writeWishlistStorageFromMutation = (items) => {
  const uniqueItems = toUniqueWishlistKeys(items);
  writeWishlistStorage(uniqueItems);
  lastLocalMutationAt = Date.now();
  return uniqueItems;
};

const getWishlistKey = (productId, variantId) => `${String(productId)}:${String(variantId)}`;

const mapPublicWishlistItemsToKeys = (items = []) => {
  const keys = items
    .map((item) => {
      const productId = Number(item?.id || 0);
      const variantId = Number(item?.variant_id || 0);
      if (!productId || !variantId) {
        return null;
      }

      return getWishlistKey(productId, variantId);
    })
    .filter(Boolean);

  return toUniqueWishlistKeys(keys);
};

const getCustomerId = () => Number(window.customer?.id || 0);

const getPublicWishlistIdentity = () => {
  const customerId =
    typeof window.SWGetCustomerWishlistId === 'function'
      ? Number(window.SWGetCustomerWishlistId() || 0)
      : getCustomerId();

  if (customerId > 0) {
    return {
      customerId,
      wishlistId: null,
      source: 'customerid'
    };
  }

  const wishlistId =
    typeof window.SWGetExpressWishlistId === 'function'
      ? String(window.SWGetExpressWishlistId() || '').trim()
      : '';

  return {
    customerId,
    wishlistId: wishlistId || null,
    source: wishlistId ? 'wishlistid' : 'none'
  };
};

const getWishlistTargetElement = (sourceElement) => {
  if (!sourceElement) {
    return null;
  }

  return (
    sourceElement.closest?.(WISHLIST_TOGGLE_SELECTOR) ||
    sourceElement.querySelector?.(WISHLIST_TOGGLE_SELECTOR) ||
    null
  );
};

const applyWishlistActiveState = (sourceElement, isActive) => {
  const targetElement = getWishlistTargetElement(sourceElement);
  if (!targetElement || !targetElement.classList) {
    return;
  }

  targetElement.classList.toggle('wishlist-active', Boolean(isActive));
  targetElement.setAttribute('data-wishlist-active', isActive ? 'true' : 'false');
  targetElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
};

const fetchPublicWishlistItems = async (identity) => {
  const params = new URLSearchParams({
    type: 'api',
    version: '1',
    _: String(Date.now())
  });

  if (identity.customerId > 0) {
    params.set('customerid', String(identity.customerId));
  } else if (identity.wishlistId) {
    params.set('wishlistid', String(identity.wishlistId));
  } else {
    throw new Error('Unable to resolve Smart Wishlist identity (customerid/wishlistid)');
  }

  const endpoint = `${window.location.origin}/a/wishlist?${params.toString()}`;
  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json, text/plain, */*'
    },
    credentials: 'include'
  });

  const responseText = await response.text();
  let responseJson = null;

  try {
    responseJson = responseText ? JSON.parse(responseText) : null;
  } catch (error) {}

  if (!response.ok) {
    throw new Error(`Smart Wishlist Public API request failed with status ${response.status}`);
  }

  const responseType = String(responseJson?.type || '').toLowerCase();
  if (responseType && responseType !== 'success') {
    throw new Error(
      `Smart Wishlist Public API responded with type=${responseJson?.type}: ${responseJson?.message || 'Unknown error'}`
    );
  }

  return Array.isArray(responseJson?.items) ? responseJson.items : [];
};

const hydrateWishlistStorageFromPublicApi = async () => {
  const hydrationStartedAt = Date.now();
  const identity = getPublicWishlistIdentity();

  if (!(identity.customerId > 0 || identity.wishlistId)) {
    return readWishlistStorage();
  }

  const items = await fetchPublicWishlistItems(identity);
  const keys = mapPublicWishlistItemsToKeys(items);

  if (lastLocalMutationAt > hydrationStartedAt) {
    return readWishlistStorage();
  }

  writeWishlistStorage(keys);

  const detail = {
    totalItems: keys.length,
    source: 'smart-wishlist-public-api',
    timestamp: Date.now()
  };

  window.dispatchEvent(new CustomEvent('wishlist:cache-updated', { detail }));

  return keys;
};

const ensurePublicWishlistHydrated = () => {
  if (publicWishlistCachePromise) {
    return publicWishlistCachePromise;
  }

  const runHydration = async () => {
    try {
      return await hydrateWishlistStorageFromPublicApi();
    } catch (error) {
      return readWishlistStorage();
    }
  };

  publicWishlistCachePromise = runHydration();
  return publicWishlistCachePromise;
};

const schedulePublicWishlistHydration = () => {
  if (hasScheduledPublicWishlistHydration || typeof window === 'undefined') {
    return;
  }

  hasScheduledPublicWishlistHydration = true;
  ensurePublicWishlistHydrated();
};

const decodeBase64Value = (value) => {
  const encoded = String(value || '').trim();
  if (!encoded) {
    return '';
  }

  try {
    return window.atob(encoded);
  } catch (error) {
    return '';
  }
};

const getApiKey = (data = {}) => {
  const sourceElement = data.element;
  const wishlistIdValue =
    sourceElement?.getAttribute?.('wishlist-id') ||
    sourceElement?.closest?.('[wishlist-id]')?.getAttribute?.('wishlist-id') ||
    '';

  const fromWishlistId = decodeBase64Value(wishlistIdValue);
  if (fromWishlistId) {
    return fromWishlistId;
  }

  const fromPayload = data.wishlistApiKey || data.settings?.wishlist_api_key;
  if (fromPayload) {
    return String(fromPayload);
  }

  return '';
};

const getVariantInput = (sourceElement) => {
  const productForm = sourceElement?.closest?.('form.shopify-product-form') || sourceElement?.closest?.('form');
  if (!productForm) {
    return null;
  }

  return productForm.querySelector('select[id^=variant-select-][name=id], select[name=id], input[name=id]');
};

const findElementByAncestorScopeSearch = (sourceElement, selector, maxDepth = 12) => {
  let node = sourceElement;
  let depth = 0;

  while (node && depth <= maxDepth) {
    if (typeof node.querySelector === 'function') {
      const found = node.querySelector(selector);
      if (found) {
        return {
          element: found,
          depth,
          container: node
        };
      }
    }

    node = node.parentElement;
    depth += 1;
  }

  return {
    element: null,
    depth: null,
    container: null
  };
};

const resolveProductContext = (data = {}) => {
  const sourceElement = data.element;
  const variantInput = getVariantInput(sourceElement);

  const closestProductElement = sourceElement?.closest?.('[data-product-id]') || null;
  const closestVariantElement = sourceElement?.closest?.('[data-variant-id]') || null;

  const scopedTileRoot =
    sourceElement?.closest?.('[data-product-tile-type-init], .product-tile-container, [class*="product-tile"], [class*="product-card"]') || null;
  const scopedProductElement = scopedTileRoot?.querySelector?.('[data-product-id]') || null;
  const scopedVariantElement = scopedTileRoot?.querySelector?.('[data-variant-id]') || null;

  const ancestorScopeProductSearch = findElementByAncestorScopeSearch(sourceElement, '[data-product-id]');
  const ancestorScopeVariantSearch = findElementByAncestorScopeSearch(sourceElement, '[data-variant-id]');

  const productIdCandidates = {
    fromData: data.productId || null,
    fromElementDataset: sourceElement?.dataset?.productId || null,
    fromClosestDataAttribute: closestProductElement?.getAttribute('data-product-id') || null,
    fromScopedTile: scopedProductElement?.getAttribute('data-product-id') || null,
    fromAncestorScopeSearch: ancestorScopeProductSearch.element?.getAttribute('data-product-id') || null
  };

  const variantIdCandidates = {
    fromData: data.variantId || null,
    fromVariantInput: variantInput?.value || null,
    fromElementDataset: sourceElement?.dataset?.variantId || null,
    fromClosestDataAttribute: closestVariantElement?.getAttribute('data-variant-id') || null,
    fromScopedTile: scopedVariantElement?.getAttribute('data-variant-id') || null,
    fromAncestorScopeSearch: ancestorScopeVariantSearch.element?.getAttribute('data-variant-id') || null
  };

  const productId =
    productIdCandidates.fromData ||
    productIdCandidates.fromElementDataset ||
    productIdCandidates.fromClosestDataAttribute ||
    productIdCandidates.fromScopedTile ||
    productIdCandidates.fromAncestorScopeSearch ||
    null;

  const variantId =
    variantIdCandidates.fromData ||
    variantIdCandidates.fromVariantInput ||
    variantIdCandidates.fromElementDataset ||
    variantIdCandidates.fromClosestDataAttribute ||
    variantIdCandidates.fromScopedTile ||
    variantIdCandidates.fromAncestorScopeSearch ||
    null;

  return {
    productId: productId ? Number(productId) : null,
    variantId: variantId ? Number(variantId) : null
  };
};

const requestWishlistAction = async ({ productId, variantId, action, apiKey, customerId }) => {
  const payload = {
    productid: Number(productId),
    variantid: Number(variantId),
    customerid: Number(customerId),
    action,
    apikey: apiKey,
    version: '1'
  };

  const formData = new window.FormData();
  Object.entries(payload).forEach(([key, value]) => {
    formData.append(key, String(value));
  });

  const endpoint = `${window.location.origin}/a/wishlist?type=api`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*'
    },
    credentials: 'include',
    body: formData
  });

  const contentType = response.headers.get('content-type') || '';
  const responseText = await response.text();

  let responseJson = null;
  if (contentType.includes('application/json') || responseText.trim().startsWith('{')) {
    try {
      responseJson = JSON.parse(responseText);
    } catch (error) {}
  }

  if (!response.ok) {
    throw new Error(`Wishlist request failed with status ${response.status}`);
  }

  const responseType = String(responseJson?.type || '').toLowerCase();
  if (responseType && responseType !== 'success') {
    const apiMessage = responseJson?.message || 'Unknown API error';
    throw new Error(`Wishlist API responded with type=${responseJson?.type}: ${apiMessage}`);
  }

  return payload;
};

export const runWishlistToggle = async (data = {}) => {
  try {
    schedulePublicWishlistHydration();

    const isSyncOnly = Boolean(data.syncOnly);

    if (isSyncOnly) {
      await ensurePublicWishlistHydrated();
    }

    const { productId, variantId } = resolveProductContext(data);
    if (!productId || !variantId) {
      throw new Error('Product and variant IDs are required for wishlist toggle');
    }

    const itemKey = getWishlistKey(productId, variantId);
    const items = readWishlistStorage();
    const isActive = items.includes(itemKey);

    applyWishlistActiveState(data.element, isActive);

    if (isSyncOnly) {
      return {
        success: true,
        action: 'sync',
        productId,
        variantId,
        isActive,
        totalItems: Array.isArray(items) ? items.length : null,
        timestamp: Date.now()
      };
    }

    const action = isActive ? 'remove' : 'add';
    const optimisticItems = items.filter((value) => value !== itemKey);
    if (action === 'add') {
      optimisticItems.push(itemKey);
    }

    const nextItems = writeWishlistStorageFromMutation(optimisticItems);
    const detail = {
      action,
      productId,
      variantId,
      isActive: action === 'add',
      totalItems: nextItems.length,
      timestamp: Date.now(),
      optimistic: true,
      apiSynced: null
    };

    applyWishlistActiveState(data.element, detail.isActive);
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail }));

    const apiKey = getApiKey(data);
    if (!apiKey) {
      throw new Error('Wishlist API key is missing');
    }

    try {
      await requestWishlistAction({
        productId,
        variantId,
        action,
        apiKey,
        customerId: getCustomerId()
      });

      detail.apiSynced = true;
    } catch (error) {
      detail.apiSynced = false;
      detail.apiError = error.message;
    }

    return {
      success: true,
      ...detail
    };
  } catch (error) {
    console.error('Wishlist Toggle error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

if (typeof window !== 'undefined') {
  schedulePublicWishlistHydration();
}

export default runWishlistToggle;
